import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { parse, stringify } from "query-string";
import { useLocation, useHistory } from "react-router-dom";
import { Search } from "../services";
import { set, cloneDeep } from "lodash";

const SearchContext = createContext();

const useSearch = () => useContext(SearchContext);

const DEFAULT_ASYNC = {
  isRequesting: false,
  isError: false,
  result: [],
  lastTerm: "",
};

const DEFAULT = {
  searchTerm: "",
  entity: "all",
  data: {
    federalCourts: { ...DEFAULT_ASYNC },
    stateCourts: { ...DEFAULT_ASYNC },
    federalJudges: { ...DEFAULT_ASYNC },
    stateJudges: { ...DEFAULT_ASYNC },
    lawFirms: { ...DEFAULT_ASYNC },
    attorneys: { ...DEFAULT_ASYNC },
    parties: { ...DEFAULT_ASYNC },
  },
};

const ENTITY_TO_API_MAP = {
  lawFirms: "law_firm",
  attorneys: "attorney",
  federalJudges: "district_judge",
  stateJudges: "state_judge",
  federalCourts: "district_court",
  stateCourts: "state_court",
  parties: "party",
};

const searchRoutes = ["/", "/results"];

const adapter = (results, key) => {
  switch (key) {
    case "federalJudges":
      return results.map((result) => ({
        id: result.cluster_id,
        federal_case_count: result.federal_case_count || 0,
        state_case_count: result.state_case_count || 0,
        lexis_state_case_count: result.lexis_state_case_count || 0,
        name: result.name || "",
        sub: result.court_abbr || "",
        es_type: result.es_type || "",
      }));
    case "federalCourts":
      return results.map((result) => ({
        id: result.court_ecf,
        federal_case_count: result.federal_case_count || 0,
        state_case_count: result.state_case_count || 0,
        lexis_state_case_count: result.lexis_state_case_count || 0,
        name: result.display_name || "",
        sub: result.court_abbr || "",
        es_type: result.es_type || "",
      }));
    case "stateCourts":
      return results.map((result) => ({
        id: result.cluster_id,
        federal_case_count: result.federal_case_count || 0,
        state_case_count: result.state_case_count || 0,
        lexis_state_case_count: result.lexis_state_case_count || 0,
        name: result.name || "",
        sub: result.state || "",
        es_type: result.es_type || "",
      }));
    case "stateJudges":
      return results.map((result) => ({
        id: result.cluster_id,
        federal_case_count: result.federal_case_count || 0,
        state_case_count: result.state_case_count || 0,
        lexis_state_case_count: result.lexis_state_case_count || 0,
        name: result.name || "",
        sub: result.court_display_name || "",
        es_type: result.es_type || "",
      }));
    case "lawFirms":
      return results.map((result) => ({
        id: result.cluster_id,
        federal_case_count: result.federal_case_count || 0,
        state_case_count: result.state_case_count || 0,
        lexis_state_case_count: result.lexis_state_case_count || 0,
        name: result.name || "",
        sub: result.state || "",
        es_type: result.es_type || "",
      }));
    case "parties":
      return results.map((result) => ({
        id: result.id,
        federal_case_count: result.federal_district_case_count,
        state_case_count: result.total_visible_lex_case_count,
        lexis_state_case_count: result.total_visible_courtlink_case_count,
        name: result.name || "",
        sub: result.state || "",
        es_type: result.es_type || "",
      }));
    case "attorneys":
    default:
      return results.map((result) => ({
        id: result.cluster_id,
        federal_case_count: result.federal_case_count || 0,
        state_case_count: result.state_case_count || 0,
        lexis_state_case_count: result.lexis_state_case_count || 0,
        name: result.name || "",
        sub: result.state || "",
        es_type: result.es_type || "",
      }));
  }
};

const SearchProvider = (props) => {
  const { search, pathname } = useLocation();
  const params = parse(search);
  const [entity, setEntity] = useState(DEFAULT.entity);
  const [searchTerm, setSearchTerm] = useState(DEFAULT.searchTerm);
  const [data, setData] = useState(
    params.q && params.q !== ""
      ? Object.keys(DEFAULT.data).reduce((acc, key) => {
          let item = { ...DEFAULT.data[key], isRequesting: true };
          acc[key] = item;
          return acc;
        }, {})
      : DEFAULT.data,
  );
  const history = useHistory();

  const controllerRef = useRef(null); //Reference to hold the AbortController instance
  const dataRef = useRef(data); //Reference to hold the data state, to prevent error on compilation
  const [currentSearch, setCurrentSearch] = useState(
    params.q && params.q !== "" ? decodeURIComponent(params.q) : "",
  ); //Used to trigger the search effect. Initialized with the decoded q param from the URL for the case when the URL is .

  const refreshParams = useCallback(
    (newParams) => {
      const newParamsStr = stringify({
        entity: DEFAULT.entity,
        ...newParams,
      });
      if (searchRoutes.includes(pathname) && stringify(params) !== newParamsStr)
        history.push(`${pathname}?${newParamsStr}`);
    },
    [history, pathname, params],
  );

  const searchMethods = useMemo(
    () => ({
      setSearchTerm: (searchTerm) => {
        setSearchTerm(searchTerm);
      },
      focus: (key) => {
        refreshParams({
          q: params.q,
          entity: key,
        });
      },
      triggerSearch: (q) => {
        if (q && q === "") return;
        let delta = {};
        for (let key in data) {
          if (key === entity || entity === "all") {
            // Only update the data for the current entity or all entities
            const dataValue = data[key];
            delta[key] = {
              ...dataValue,
              isRequesting: true,
            };
          }
        }
        if (
          Object.keys(delta).length > 0 &&
          (currentSearch !== searchTerm || entity !== params.entity)
        ) {
          //check if the search term or entity has changed
          setCurrentSearch(decodeURIComponent(q)); //Prepare the new search term to trigger the effect
          setData({
            ...data,
            ...delta,
          });
        }
      },
    }),
    [refreshParams, params, data, entity, currentSearch, searchTerm],
  );

  /*
    This useEffect that ensures if the entity param in the
    address bar changes, the entity state will stay in sync.
  */
  useEffect(() => {
    if (params.entity && params.entity !== entity) {
      setEntity(params.entity);
      if (params.q && params.q !== "") searchMethods.triggerSearch(params.q); // Trigger search with the new entity
    }
  }, [params, entity, setEntity, searchMethods]);

  /*
    This useEffect is responsible for fetching the search results
    when the search term or entity changes.
  */
  useEffect(() => {
    if (!currentSearch && currentSearch === "") return; // Prevent search if the newSearchParam is empty
    if (controllerRef.current) controllerRef.current.abort(); // Abort any ongoing request

    const controller = new AbortController();
    controllerRef.current = controller;
    const signal = controller.signal;

    for (let entityKey in dataRef.current) {
      if (entity === "all" || entity === entityKey) {
        //check if the entity is "all" or matches the current entityKey to start the search
        Search.get(
          currentSearch,
          ENTITY_TO_API_MAP[entityKey],
          undefined,
          signal,
        )
          .then((res) => {
            setData((prevData) => {
              const courtDataCopy = cloneDeep(prevData);
              set(courtDataCopy, entityKey, {
                result: adapter(res.result, entityKey),
              });
              return courtDataCopy;
            }); //save the results in the data state, get the results from the other requests for this search term
          })
          .catch((error) => {
            if (error.code !== "ERR_CANCELED") {
              // If the request was aborted, we don't want to update the state, because the triggerSearch will handle the new request and states
              setData((prevData) => {
                const courtDataCopy = cloneDeep(prevData);
                set(courtDataCopy, entityKey, { isRequesting: false });
                return courtDataCopy;
              });
            }
          });
      }
    }

    return () => {
      controller.abort(); // Cleanup function to abort the request if the component unmounts or if the effect runs again
    };
  }, [currentSearch, entity]); // Only use the currentSearch and entity as dependencies to avoid unnecessary re-renders

  const value = {
    entity,
    searchTerm: [searchTerm, searchMethods.setSearchTerm],
    q: params.q,
    focus: searchMethods.focus,
    updateFilters: searchMethods.updateFilters,
    triggerSearch: searchMethods.triggerSearch,
    getActiveFilterKey: searchMethods.getActiveFilterKey,
    data,
  };

  return (
    <SearchContext.Provider value={value}>
      {props.children}
    </SearchContext.Provider>
  );
};

export { SearchProvider, useSearch };
